import { useCallback, useEffect } from 'react';
import { pcidAuth } from 'Utils/sso/auth';
import { SSO } from '@/data/constants';
import { getLastViewedPage, refreshTokenFlow, saveTokens } from 'Utils/auth';
import { Language, decodeState } from '@pcid/web-sdk';
import { isPharmaprixBanner } from '@/utils';
import { Spinner } from 'Shared/spinner';
import * as Sentry from '@sentry/react';

export default function LoginRedirect() {
  const loadSSO = useCallback(async (intent: string, isOneCheckout: boolean) => {
    try {
      // we need to redirect to the SSO create account or login function since one checkout has both register and login buttons
      window.location.href =
        isOneCheckout && intent === 'register'
          ? await pcidAuth.pcidCreateAccountFunction()
          : await pcidAuth.pcidLoginFunction();
    } catch (err) {
      Sentry.captureMessage('SSO log in failed ' + err);
    }
  }, []);

  const loadToken = useCallback(async (code: string, state: string, isOneCheckout: boolean, postLoginUrl: string) => {
    try {
      const tokens = await pcidAuth.handleSignIn(code, state);

      if (tokens !== null) {
        // save tokens to cookies
        saveTokens(tokens, isOneCheckout);

        // Create session after login (if enabled)
        // Session cookies will be set by API response
        if (process.env.NEXT_PUBLIC_SESSION_MANAGEMENT_ENABLED === 'true') {
          try {
            await fetch('/api/pcid/session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(tokens),
              credentials: 'include',
            });
          } catch (sessionErr) {
            // Log but don't fail - session will be created on next SSR
            console.warn('Failed to create session after login:', sessionErr);
          }
        }

        const lastViewedPage = getLastViewedPage();
        if (isOneCheckout && postLoginUrl) {
          // Use the provided postLoginUrl for one-checkout flow
          const decodedPostLoginUrl = decodeURIComponent(postLoginUrl);
          window.location.href = decodedPostLoginUrl;
        } else {
          window.location.href = lastViewedPage;
        }
        Sentry.captureMessage(`SSO log in Successfully`);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleOneCheckoutRefreshFlow = useCallback(
    async (isOneCheckout: boolean, postLoginUrl: string) => {
      try {
        // Attempt to refresh the token using the existing refresh token
        const newTokens = await refreshTokenFlow();

        if (newTokens !== null) {
          saveTokens(newTokens, isOneCheckout);
          if (isOneCheckout && postLoginUrl) {
            const decodedPostLoginUrl = decodeURIComponent(postLoginUrl);
            window.location.href = decodedPostLoginUrl;
          }

          Sentry.captureMessage('Token refresh for one checkout is successful');
        } else {
          // If refresh fails, redirect to login flow
          Sentry.captureMessage('Token refresh for one checkout failed, redirecting to login');
          loadSSO('login', isOneCheckout);
        }
      } catch (err) {
        console.error('Refresh flow for one checkout error:', err);
        Sentry.captureMessage('Refresh flow for one checkout failed: ' + err);
        // Fallback to login flow if refresh fails
        loadSSO('login', isOneCheckout);
      }
    },
    [loadSSO]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOneCheckout = urlParams.get('isOneCheckout') || 'false';
    const postLoginUrl = urlParams.get('postLoginUrl') || '';

    pcidAuth.setLanguage(isPharmaprixBanner() ? Language.FR : Language.EN);
    pcidAuth.setRedirectUrl(`${window.location.origin}/${SSO.LOGIN}`);
    pcidAuth.setCustomProps({
      postLoginUrl,
      isOneCheckout,
    });
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get(SSO.CODE) || '';
    let state = urlParams.get(SSO.STATE) || '';
    const isOneCheckout = urlParams.get('isOneCheckout') || 'false';
    const intent = urlParams.get('intent') || '';
    const postLoginUrl = urlParams.get('postLoginUrl') || '';

    if (isOneCheckout === 'true' && intent === 'refresh') {
      handleOneCheckoutRefreshFlow(isOneCheckout === 'true', postLoginUrl);
      return;
    }

    if (code === '' && state === '') {
      loadSSO(intent, isOneCheckout === 'true');
    } else {
      state = decodeURIComponent(state);
      const decodedState = decodeState(state);
      const { postLoginUrl: decodedPostLoginUrl, isOneCheckout: decodedIsOneCheckout } =
        decodedState.customProps as unknown as {
          postLoginUrl: string;
          isOneCheckout: string;
        };
      loadToken(code, state, decodedIsOneCheckout === 'true', decodedPostLoginUrl);
    }
  }, [handleOneCheckoutRefreshFlow, loadSSO, loadToken]);

  return <Spinner />;
}
